import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Landmark, Save, Loader2, Plus, Trash2, ExternalLink, ClipboardPaste, Layers } from "lucide-react";

function LinkRow({ row, onSaved, onDeleted }) {
  const [label, setLabel] = useState(row.label || "");
  const [url, setUrl] = useState(row.url || "");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const dirty = label !== (row.label || "") || url !== (row.url || "");
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put(`/state-court-links/${row.state}`, { label, url }); onSaved(data); toast.success(`Saved ${row.state} court link`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };
  const del = async () => {
    if (!window.confirm(`Remove the ${row.state} court link?`)) return;
    setDeleting(true);
    try { await api.delete(`/state-court-links/${row.state}`); onDeleted(row.state); toast.success(`Removed ${row.state}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
    finally { setDeleting(false); }
  };
  return (
    <tr data-testid={`court-link-row-${row.state}`} className="border-b border-slate-100 align-top">
      <td className="px-3 py-2"><span className="inline-flex h-7 w-8 items-center justify-center rounded bg-[#1A395E] text-[12px] font-bold text-white">{row.state}</span></td>
      <td className="px-3 py-2">
        <input data-testid={`court-link-label-${row.state}`} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Link label"
          className="w-full rounded border border-slate-300 px-2 py-1.5 text-[13px] focus:border-teal focus:outline-none" />
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1.5">
          <input data-testid={`court-link-url-${row.state}`} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…"
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-[12px] font-mono focus:border-teal focus:outline-none" />
          {url && <a href={url} target="_blank" rel="noopener noreferrer" className="shrink-0 text-slate-400 hover:text-teal" title="Open"><ExternalLink className="h-4 w-4" /></a>}
        </div>
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-right">
        <div className="inline-flex items-center gap-1.5">
          <button data-testid={`court-link-save-${row.state}`} onClick={save} disabled={!dirty || saving}
            className="inline-flex items-center gap-1 rounded-md bg-[#1A395E] px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-[#24497A] transition-colors disabled:opacity-40">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
          </button>
          <button data-testid={`court-link-delete-${row.state}`} onClick={del} disabled={deleting} title="Remove"
            className="inline-flex items-center rounded-md border border-rose-200 p-1.5 text-rose-500 hover:bg-rose-50 transition-colors disabled:opacity-40">
            {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function CourtLinks() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nState, setNState] = useState("");
  const [nLabel, setNLabel] = useState("");
  const [nUrl, setNUrl] = useState("");
  const [adding, setAdding] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [loadingPack, setLoadingPack] = useState(false);
  const loadPack = async () => {
    if (!window.confirm("Load the 50-state court link pack? Existing states you've already added or edited are kept — only missing states are added.")) return;
    setLoadingPack(true);
    try {
      const { data } = await api.post("/state-court-links/load-pack", { overwrite: false });
      await load();
      toast.success(`Added ${data.added_count} states · ${data.skipped_count} kept as-is`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load pack"); }
    finally { setLoadingPack(false); }
  };

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/state-court-links"); setRows(data || []); }
    catch { toast.error("Could not load court links"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onSaved = (u) => setRows((rs) => rs.map((r) => r.state === u.state ? { ...r, ...u } : r));
  const onDeleted = (st) => setRows((rs) => rs.filter((r) => r.state !== st));
  const addRow = async () => {
    const st = nState.trim().toUpperCase();
    if (st.length !== 2) { toast.error("Enter a 2-letter state code"); return; }
    setAdding(true);
    try {
      const { data } = await api.put(`/state-court-links/${st}`, { label: nLabel, url: nUrl });
      setRows((rs) => [...rs.filter((r) => r.state !== st), data].sort((a, b) => a.state.localeCompare(b.state)));
      setNState(""); setNLabel(""); setNUrl("");
      toast.success(`Added ${st}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
    finally { setAdding(false); }
  };

  const importBulk = async () => {
    if (!bulkText.trim()) { toast.error("Paste some lines first"); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/state-court-links/bulk", { text: bulkText });
      await load();
      if (data.added_count) toast.success(`Imported ${data.added_count} link${data.added_count > 1 ? "s" : ""}`);
      if (data.errors?.length) toast.error(`${data.errors.length} line(s) skipped — check format`);
      if (data.added_count) { setBulkText(""); setBulkOpen(false); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-3xl font-700 tracking-tight text-slate-800 flex items-center gap-2">
          <Landmark className="h-7 w-7 text-teal" /> State Court Links
        </h1>
        <p className="text-sm text-slate-500 mt-1 max-w-3xl">
          Manage the direct court/case-search links shown under <b>Federal &amp; Legal Research</b> in every order's
          Quick Research card. Links appear automatically when an order's state matches.
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button data-testid="bulk-import-toggle" onClick={() => setBulkOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#1A395E] hover:text-teal transition-colors">
            <ClipboardPaste className="h-4 w-4" /> Bulk import (paste a list) {bulkOpen ? "▲" : "▼"}
          </button>
          <button data-testid="load-pack-btn" onClick={loadPack} disabled={loadingPack}
            className="inline-flex items-center gap-1.5 rounded-lg border border-teal/60 bg-teal/5 px-3.5 py-2 text-[12px] font-semibold text-teal hover:bg-teal/10 transition-colors disabled:opacity-60">
            {loadingPack ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Layers className="h-3.5 w-3.5" />} Load 50-state pack
          </button>
        </div>
        {bulkOpen && (
          <div className="mt-3">
            <p className="text-[12px] text-slate-500 mb-1.5">One per line: <code className="rounded bg-slate-100 px-1">STATE | Label | URL</code> (also accepts tabs or commas). Existing states are updated.</p>
            <textarea data-testid="bulk-import-text" value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={6}
              placeholder={"PA | PA Unified Judicial System | https://ujsportal.pacourts.us/CaseSearch\nTX | Texas.gov Court Records | https://www.txcourts.gov/"}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-[12px] font-mono focus:border-teal focus:outline-none" />
            <button data-testid="bulk-import-btn" onClick={importBulk} disabled={importing}
              className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-[#1A395E] px-4 py-2 text-[12px] font-semibold text-white hover:bg-[#24497A] transition-colors disabled:opacity-60">
              {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ClipboardPaste className="h-3.5 w-3.5" />} Import links
            </button>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : (
          <table className="w-full text-sm" data-testid="court-links-table">
            <thead className="text-[11px] uppercase tracking-wide text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="px-3 py-2 text-left">State</th>
                <th className="px-3 py-2 text-left">Label</th>
                <th className="px-3 py-2 text-left">URL</th>
                <th className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">No links yet — add one below.</td></tr>}
              {rows.map((r) => <LinkRow key={r.state} row={r} onSaved={onSaved} onDeleted={onDeleted} />)}
            </tbody>
          </table>
        )}

        <div data-testid="court-link-add" className="mt-4 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3">
          <div className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold text-[#1A395E]"><Plus className="h-3.5 w-3.5" /> Add a state court link</div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-[11px] font-semibold text-slate-500">State
              <input data-testid="court-link-new-state" value={nState} onChange={(e) => setNState(e.target.value.toUpperCase().slice(0, 2))} placeholder="e.g. PA" maxLength={2}
                className="mt-0.5 block w-16 rounded border border-slate-300 px-2 py-1.5 text-[13px] uppercase focus:border-teal focus:outline-none" /></label>
            <label className="flex-1 min-w-[180px] text-[11px] font-semibold text-slate-500">Label
              <input data-testid="court-link-new-label" value={nLabel} onChange={(e) => setNLabel(e.target.value)} placeholder="e.g. PA Unified Judicial System Search"
                className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1.5 text-[13px] focus:border-teal focus:outline-none" /></label>
            <label className="flex-1 min-w-[220px] text-[11px] font-semibold text-slate-500">URL
              <input data-testid="court-link-new-url" value={nUrl} onChange={(e) => setNUrl(e.target.value)} placeholder="https://…"
                className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1.5 text-[12px] font-mono focus:border-teal focus:outline-none" /></label>
            <button data-testid="court-link-add-btn" onClick={addRow} disabled={adding}
              className="inline-flex items-center gap-1.5 rounded-md bg-teal px-3.5 py-2 text-[12px] font-semibold text-teal-foreground hover:bg-teal/90 transition-colors disabled:opacity-60">
              {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
